"use client";

import { ArrowRight, Check, Calculator, Atom, Brain } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CoreMasteryTracks() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const disciplines = [{
    title: "Calculus",
    subtitle: "AP & COLLEGE LEVEL",
    icon: "calculus",
    description: "Master limits, derivatives, and integrals with precision. We break down complex theorems into intuitive, actionable steps for your exams.",
    skills: ["Differential Calculus mastery", "Integral applications", "Series & sequences logic", "Exam-day problem solving"],
    popular: false
  }, {
    title: "AP Physics",
    subtitle: "MECHANICS & E&M",
    icon: "guitar",
    description: "Navigate the laws of motion and electromagnetism. Our approach focuses on conceptual understanding to solve even the most challenging FRQs.",
    skills: ["Kinematics & dynamics", "Work, energy & power", "Circuit analysis", "Lab data interpretation"],
    popular: true
  }, {
    title: "SAT Math",
    subtitle: "STRATEGY & SPEED",
    icon: "mic",
    description: "Optimize your test-taking strategy. We focus on pattern recognition and time-management techniques to maximize your score on every section.",
    skills: ["Algebraic manipulation", "Advanced geometry logic", "Data analysis shortcuts", "Timed practice simulations"],
    popular: false
  }];
  const renderIcon = (icon: string) => {
    if (icon === "calculus") return <Calculator className="w-20 h-20 text-secondary" strokeWidth={1.25} />;
    if (icon === "guitar") return <Atom className="w-20 h-20 text-secondary" strokeWidth={1.25} />;
    return <Brain className="w-20 h-20 text-secondary" strokeWidth={1.25} />;
  };
  return <section data-section-id="244" id="core-mastery-tracks" className="bg-background text-foreground pt-24 md:pt-32 lg:pt-40 pb-20 md:pb-24 lg:pb-30">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div ref={header.ref} className={`text-center mb-16 md:mb-20 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-primary mb-6">
            Curriculum / Apex.01
          </p>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-6xl font-medium text-foreground leading-[1.1] tracking-tight mb-6">
            Select Your{" "}
            <span className="italic text-secondary font-light">Mastery Track</span>
          </h2>
          <p className="font-default text-base md:text-lg text-muted-foreground max-w-[600px] mx-auto leading-relaxed">
            From foundational concepts to elite exam performance — explore our specialized STEM tracks designed for academic ascent.
          </p>
        </div>

        <div ref={grid.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 transition-all duration-700 ease-out delay-150 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {disciplines.map((item, i) => <div data-index={i} key={item.title} className="group relative flex flex-col bg-card border border-border rounded-lg p-10 transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-lg overflow-hidden">
              {item.popular && <Badge className="absolute top-6 right-6 bg-primary text-primary-foreground font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-lg shadow-sm z-10">
                  High Demand
                </Badge>}

              <div className="aspect-square w-full bg-accent rounded-lg flex items-center justify-center mb-8 overflow-hidden transition-transform duration-500 ease-out group-hover:scale-[1.02]">
                <div className="transition-transform duration-500 ease-out group-hover:scale-110">
                  {renderIcon(item.icon)}
                </div>
              </div>

              <div className="mb-5">
                <h3 className="font-serif font-default text-[40px] leading-[1.05] font-medium text-foreground mb-2 tracking-tight">
                  {item.title}
                </h3>
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-primary">
                  {item.subtitle}
                </p>
              </div>

              <p className="font-default text-base text-muted-foreground leading-relaxed mb-6">
                {item.description}
              </p>

              <div className="relative h-px bg-primary/40 w-16 mb-6 transition-all duration-500 ease-out group-hover:w-full" />

              <ul className="flex flex-col gap-3 mb-8">
                {item.skills.map((skill, j) => <li data-index={j} key={skill} className="flex items-start gap-3 font-default text-sm text-foreground/85">
                    <span className="mt-2 w-1.5 h-1.5 rounded-lg bg-primary shrink-0" />
                    <span className="leading-relaxed">{skill}</span>
                  </li>)}
              </ul>

              <div className="mt-auto">
                <Button asChild variant="link" className="p-0 h-auto font-mono text-xs tracking-[0.2em] uppercase text-foreground hover:text-primary no-underline hover:no-underline">
                  <Link to="/MasteryTracksPage" className="inline-flex items-center gap-2">
                    View Syllabus
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}